package com.siriusxm.unifiedcontent.console.player

import com.siriusxm.pia.player.AudioTrack
import com.siriusxm.pia.player.CMCPlayer
import com.siriusxm.pia.player.PlaybackClock
import com.siriusxm.pia.player.PlaybackDetailsUpdate
import com.siriusxm.unifiedcontent.cmc.CMCService
import com.siriusxm.unifiedcontent.cmc.DirectPlaybackRequest
import com.siriusxm.unifiedcontent.cmc.PlaybackAccess
import com.siriusxm.unifiedcontent.cmc.PlaybackDescriptor
import com.siriusxm.unifiedcontent.cmc.PlaybackProtocol
import com.siriusxm.unifiedcontent.cmc.PlaybackRequest
import com.siriusxm.unifiedcontent.cmc.PlaybackStream
import contentingestion.unifiedmodel.LinearCut
import io.ktor.client.plugins.ClientRequestException
import kotlinx.coroutines.CancellationException
import kotlin.time.Instant

/**
 * The SDK-backed implementation of [CMCPlayer] — the headless player an application drives itself
 * (UCA-1111). It owns a private [AudioPlayerController] (the hls.js/`<audio>` engine, with no rendered
 * UI) and resolves playback through the CMC SDK exactly as the global bar does: [playEntity] calls
 * `preparePlayback`, [playDetails] calls `prepareDirectPlayback`. The prepare round-trip,
 * descriptor-to-[AudioTrack] mapping, and the credential-refresh closure are all internal, so the
 * caller supplies only an entity id (or direct details) and observes/controls playback through
 * [controller].
 *
 * Each instance is independent of the global player bar and of every other [CMCPlayer]; the app that
 * creates it owns its lifecycle and must [dispose] it, which tears down the underlying controller.
 *
 * The host obtains one from `MediaPlayer.createPlayer()`. An embedded (iframe) application cannot reach
 * that across the frame boundary, so it constructs one directly with its own [CMCService] (UCA-1133).
 * The prepare call then runs from the embedded frame, which is same-origin with the host, so the
 * CloudFront cookies it sets apply to that frame's media requests.
 *
 * Changing presentation details for linear channels are looked up in the browser through the optional
 * `channelCuts` function (Aggregator mapping plus EPG cuts, see [channelCutsLookup]); the server has no
 * details operation. Without it, channels simply report no details.
 */
public class SdkCMCPlayer(
    /**
     * Resolves the CMC SDK on demand rather than at construction. The global bar's [SdkCMCPlayer] is
     * built eagerly during composition — before [com.siriusxm.pia.views.unifiedaggregator.AggregatorApplication]
     * is initialized — so touching `aggregatorApp.cmcService` (which reads its `lateinit context`) in the
     * constructor threw and blanked the whole app. Deferring the lookup to play time, when the context is
     * always set, keeps construction free of that dependency.
     */
    private val cmcService: () -> CMCService,
    /**
     * Turns the server-selected artwork path into a loadable URL for the track. The server returns a
     * raw aggregator storage key, and building the CDN URL needs per-environment configuration that
     * only the host has, so the host injects its resolver. The default passes the value through
     * unchanged, which suits callers that do not render artwork.
     */
    private val artworkUrl: (String?) -> String? = { it },
    /** Host-owned handling of failed preparation renewal, e.g. prompting to reconnect a source. */
    private val onRefreshFailure: (String, Throwable) -> Unit = { _, _ -> },
    /**
     * Returns the most recent cuts for a linear channel's source id; null disables channel details.
     * Failures other than 401/403/404 propagate so the controller retries; those three mean the
     * details are unsupported or denied and polling stops.
     */
    private val channelCuts: (suspend (sourceChannelId: String) -> List<LinearCut>)? = null,
) : CMCPlayer {
    override val controller: AudioPlayerController = AudioPlayerController()

    override suspend fun playEntity(entityId: String, startSeconds: Double) {
        val descriptor = cmcService().preparePlayback(PlaybackRequest(entityId = entityId))
        val source = descriptor.items.first().metadata
        val detailsLoader: (suspend (PlaybackClock) -> PlaybackDetailsUpdate)? =
            if (source.detailsAvailable == true && source.entityType == "channel-linear" && channelCuts != null) {
                { clock -> loadChannelDetails(channelCuts, source.entityId, source.imageUrl, clock) }
            } else null
        controller.play(
            descriptor.toAudioTrack(
                startSeconds = startSeconds,
                refresh = { refreshEntity(entityId) },
                detailsLoader = detailsLoader,
            )
        )
    }

    override suspend fun playDetails(
        type: String,
        url: String,
        title: String?,
        subTitle: String?,
        imageUrl: String?,
        durationMs: Long?,
        startSeconds: Double,
    ) {
        val request = DirectPlaybackRequest(
            entityType = type,
            url = url,
            title = title,
            subtitle = subTitle,
            imageUrl = imageUrl,
            durationMilliseconds = durationMs,
        )
        val descriptor = cmcService().prepareDirectPlayback(request)
        controller.play(descriptor.toAudioTrack(startSeconds, refresh = { refreshDirect(request) }))
    }

    override fun dispose() {
        controller.dispose()
    }

    /**
     * Fetch a linear channel's recent cuts in the browser and build the details update. Unsupported or
     * denied sources (401/403/404) stop polling; cancellation and other failures propagate.
     */
    private suspend fun loadChannelDetails(
        cuts: suspend (String) -> List<LinearCut>,
        channelId: String,
        channelImageUrl: String?,
        clock: PlaybackClock,
    ): PlaybackDetailsUpdate {
        val recent = try {
            cuts(channelId)
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (error: ClientRequestException) {
            if (error.response.status.value in listOf(401, 403, 404)) {
                return PlaybackDetailsUpdate(pollAfterMilliseconds = null)
            }
            throw error
        }
        return channelPlaybackDetails(recent, channelImageUrl, clock, artworkUrl)
    }

    /** Re-prepare an entity to renew short-lived credentials; returns the fresh expiry, or null. */
    private suspend fun refreshEntity(entityId: String): Instant? = try {
        cmcService().preparePlayback(PlaybackRequest(entityId = entityId)).refreshedExpiry()
    } catch (cancellation: kotlinx.coroutines.CancellationException) {
        throw cancellation
    } catch (error: Throwable) {
        onRefreshFailure(entityId, error)
        throw error
    }

    /** Re-prepare direct details to renew short-lived credentials; returns the fresh expiry, or null. */
    private suspend fun refreshDirect(request: DirectPlaybackRequest): Instant? =
        cmcService().prepareDirectPlayback(request).refreshedExpiry()

    /**
     * Map a prepared [PlaybackDescriptor] to the engine's [AudioTrack], starting [startSeconds] in. The
     * presentation fields come from the item metadata (falling back to the requested entity), and
     * credential refresh is wired only for cookie-backed streams: the stream URL is stable across a
     * refresh, so [refresh] simply re-issues the same-origin CloudFront cookies while the current stream
     * keeps playing. Direct/pre-signed streams get no refresh loop.
     */
    private fun PlaybackDescriptor.toAudioTrack(
        startSeconds: Double,
        refresh: suspend () -> Instant?,
        detailsLoader: (suspend (PlaybackClock) -> PlaybackDetailsUpdate)? = null,
    ): AudioTrack {
        val stream = firstSupportedStream()
        val metadata = items.first().metadata
        val cookieBacked = stream.access == PlaybackAccess.RESPONSE_COOKIES
        return AudioTrack(
            url = stream.url,
            title = metadata.title ?: requestedEntity.title,
            subTitle = metadata.subtitle ?: requestedEntity.subtitle,
            imageUrl = artworkUrl(metadata.imageUrl ?: requestedEntity.imageUrl),
            credentialsExpireAt = stream.expiresAt?.takeIf { cookieBacked },
            refreshCredentials = if (cookieBacked) refresh else null,
            startSeconds = startSeconds,
            isLive = metadata.isLive == true,
            loadDetails = detailsLoader,
        )
    }
}

/** The fresh credential expiry from a re-prepared descriptor, or null when no longer cookie-backed. */
private fun PlaybackDescriptor.refreshedExpiry(): Instant? {
    val stream = firstSupportedStream()
    return stream.expiresAt?.takeIf { stream.access == PlaybackAccess.RESPONSE_COOKIES }
}

/** The first HLS or direct-audio stream in the descriptor, or an error when there is none. */
private fun PlaybackDescriptor.firstSupportedStream(): PlaybackStream {
    val item = items.firstOrNull() ?: error("The playback description contains no items")
    return item.streams.firstOrNull {
        it.protocol == PlaybackProtocol.HLS || it.protocol == PlaybackProtocol.DIRECT_AUDIO
    } ?: error("The playback description contains no supported audio streams")
}
